"use client";

import { Mail, Send, Inbox, Clock, CheckCircle, XCircle, MousePointerClick, Eye } from "lucide-react";
import type React from "react";
import { useRouter } from "next/navigation";

import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@/app/_module/components/ui/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/app/_module/components/ui/table";
import { DataPagination, type PaginationMeta } from "@/app/_module/components/ui/data-pagination";
import type { EmailHistory } from "@/app/(authenticated)/communication/_modules/types/email-history.types";

interface LeadEmailHistoryProps {
    leadId: string;
    emails?: EmailHistory[];
    isLoading?: boolean;
    oauthConfigured?: boolean;
    onEmailClick?: (email: EmailHistory) => void;
    paginationMeta?: PaginationMeta;
    onPageChange?: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
    embedded?: boolean; // When true, hide outer Card wrapper
}

export const LeadEmailHistory: React.FC<LeadEmailHistoryProps> = function ({
    leadId,
    emails = [],
    isLoading = false,
    oauthConfigured = true,
    onEmailClick,
    paginationMeta,
    onPageChange,
    onPageSizeChange,
    embedded = false,
}) {
    const router = useRouter();
    const getStatusIcon = (status: string) => {
        switch (status) {
            case "sent":
                return <Send className="h-4 w-4 text-blue-600" />;
            case "delivered":
                return <CheckCircle className="h-4 w-4 text-green-600" />;
            case "received":
                return <CheckCircle className="h-4 w-4 text-green-600" />;
            case "opened":
                return <Eye className="h-4 w-4 text-purple-600" />;
            case "clicked":
                return <MousePointerClick className="h-4 w-4 text-indigo-600" />;
            case "failed":
            case "bounced":
                return <XCircle className="h-4 w-4 text-red-600" />;
            default:
                return <Mail className="h-4 w-4 text-gray-600" />;
        }
    };

    const getStatusBadge = (status: string) => {
        const variants = {
            sent: "bg-blue-100 text-blue-800",
            delivered: "bg-green-100 text-green-800",
            received: "bg-green-100 text-green-800",
            opened: "bg-purple-100 text-purple-800",
            clicked: "bg-indigo-100 text-indigo-800",
            failed: "bg-red-100 text-red-800",
            bounced: "bg-orange-100 text-orange-800",
        };

        return (
            <Badge className={variants[status as keyof typeof variants] || "bg-gray-100 text-gray-800"}>
                {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
            </Badge>
        );
    };

    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const truncateText = (text: string | null | undefined, maxLength: number) => {
        if (!text) return "-";
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + "...";
    };

    // Helper to get email status (backend returns emailStatus, type expects status as alias)
    const getEmailStatus = (email: EmailHistory): string => {
        return email.emailStatus || email.status || 'unknown';
    };

    if (isLoading) {
        const loadingContent = (
            <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading email history...</span>
            </div>
        );

        if (embedded) return loadingContent;

        return (
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Mail className="h-5 w-5" />
                        Email History
                    </CardTitle>
                </CardHeader>
                <CardContent>{loadingContent}</CardContent>
            </Card>
        );
    }

    // Compact card-based view for embedded mode
    if (embedded) {
        if (!oauthConfigured) {
            return (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                    <Mail className="h-10 w-10 text-gray-400 mb-3" />
                    <p className="text-sm text-gray-600 mb-2">Email not configured</p>
                    <Button
                        size="sm"
                        onClick={() => router.push('/settings?tab=account')}
                        className="bg-blue-600 hover:bg-blue-700"
                    >
                        Connect Email
                    </Button>
                </div>
            );
        }

        if (emails.length === 0) {
            return (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                    <Mail className="h-10 w-10 text-gray-400 mb-3" />
                    <p className="text-sm text-gray-600">No emails yet</p>
                </div>
            );
        }

        return (
            <div className="space-y-2">
                {emails.map((email) => (
                    <div
                        key={email.id}
                        className="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer"
                        onClick={() => onEmailClick?.(email)}
                    >
                        <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                                {email.emailType === "inbound" ? (
                                    <Inbox className="h-4 w-4 text-green-600 shrink-0" />
                                ) : (
                                    <Send className="h-4 w-4 text-blue-600 shrink-0" />
                                )}
                                <div className="min-w-0">
                                    <p className="text-sm font-medium truncate">
                                        {truncateText(email.subject, 35) || "(No subject)"}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">
                                        {email.emailType === "outbound"
                                            ? truncateText(email.toEmail, 25)
                                            : truncateText(email.fromEmail, 25)
                                        }
                                    </p>
                                </div>
                            </div>
                            <div className="shrink-0">
                                {getStatusBadge(getEmailStatus(email))}
                            </div>
                        </div>
                        <div className="flex items-center justify-between mt-2 pt-2 border-t">
                            <p className="text-xs text-gray-500">
                                {formatDate(email.sentAt)}
                            </p>
                            <div className="flex items-center gap-1">
                                {email.openedAt && (
                                    <Badge variant="outline" className="text-purple-600 text-xs py-0 px-1.5">
                                        <Eye className="h-3 w-3 mr-0.5" />
                                        Opened
                                    </Badge>
                                )}
                                {email.clickedAt && (
                                    <Badge variant="outline" className="text-indigo-600 text-xs py-0 px-1.5">
                                        <MousePointerClick className="h-3 w-3 mr-0.5" />
                                        Clicked
                                    </Badge>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    const mainContent = (
        <>
            {!oauthConfigured ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                    <Mail className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                        Email integration not configured
                    </h3>
                    <p className="text-gray-600 mb-4">
                        Connect your Gmail or Outlook account to view email history
                    </p>
                    <Button
                        onClick={() => router.push('/settings?tab=account')}
                        className="bg-blue-600 hover:bg-blue-700"
                    >
                        <Mail className="h-4 w-4 mr-2" />
                        Connect Email
                    </Button>
                </div>
            ) : emails.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                    <Mail className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No emails yet</h3>
                    <p className="text-gray-600 mb-4">
                        Email history will appear here once you start sending emails to this lead.
                    </p>
                    <Button className="bg-blue-600 hover:bg-blue-700">
                        <Mail className="h-4 w-4 mr-2" />
                        Send First Email
                    </Button>
                </div>
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Type</TableHead>
                                    <TableHead>From/To</TableHead>
                                    <TableHead>Subject</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Engagement</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {emails.map((email) => (
                                    <TableRow
                                        key={email.id}
                                        className="cursor-pointer hover:bg-gray-50"
                                        onClick={() => onEmailClick?.(email)}
                                    >
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                {email.emailType === "inbound" ? (
                                                    <Inbox className="h-4 w-4 text-green-600" />
                                                ) : (
                                                    <Send className="h-4 w-4 text-blue-600" />
                                                )}
                                                <span className="capitalize">{email.emailType}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="font-medium">
                                            <div className="flex flex-col">
                                                <span className="text-sm">
                                                    {email.emailType === "outbound"
                                                        ? truncateText(email.toEmail, 30)
                                                        : truncateText(email.fromEmail, 30)
                                                    }
                                                </span>
                                                {email.ccEmails && email.ccEmails.length > 0 && (
                                                    <span className="text-xs text-gray-500">
                                                        CC: {email.ccEmails.length}
                                                    </span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="max-w-xs">
                                                <div className="font-medium text-sm">
                                                    {truncateText(email.subject, 40)}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                {getStatusIcon(getEmailStatus(email))}
                                                {getStatusBadge(getEmailStatus(email))}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1">
                                                <Clock className="h-4 w-4 text-gray-400" />
                                                <span className="text-sm">
                                                    {formatDate(email.sentAt)}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                {email.openedAt && (
                                                    <Badge variant="outline" className="text-purple-600 text-xs">
                                                        <Eye className="h-3 w-3 mr-1" />
                                                        Opened
                                                    </Badge>
                                                )}
                                                {email.clickedAt && (
                                                    <Badge variant="outline" className="text-indigo-600 text-xs">
                                                        <MousePointerClick className="h-3 w-3 mr-1" />
                                                        Clicked
                                                    </Badge>
                                                )}
                                                {!email.openedAt && !email.clickedAt && getEmailStatus(email) === "delivered" && (
                                                    <span className="text-gray-400 text-xs">No engagement</span>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>

                    {paginationMeta && onPageChange && !embedded && (
                        <DataPagination
                            meta={paginationMeta}
                            onPageChange={onPageChange}
                            onPageSizeChange={onPageSizeChange}
                            isLoading={isLoading}
                            showPageSize={true}
                        />
                    )}
                </>
            )}
        </>
    );

    if (embedded) return mainContent;

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    Email History ({paginationMeta?.totalItems ?? emails.length})
                </CardTitle>
            </CardHeader>
            <CardContent>
                {mainContent}
            </CardContent>
        </Card>
    );
};

export default LeadEmailHistory;
